// web_components/wcCase/wcCase_Renderer.ts

import {
	getPricingDetails,
	getProductTypeDetails,
	getStockStatus,
	wrapCasePattern,
} from './wcCase_Utils';

// web_components/wcCase/wcCase_Renderer.ts

interface Bottle {
	product_image_url: string;
	product_name: string;
	product_type: string;
	rating_count: number;
	average_rating: number;
	bottle_quantity: number;
	country: string;
	grape_type: string;
	wine_style: string;
	spirit_type: string;
}

/**
 * Generates the HTML for the case with components based on the provided product data.
 * Handles pricing, discount, stock status, product type, and more.
 *
 * @param {object} dataAttributes - The case data to render.
 * @param {string} instanceId - A unique instance ID for generating unique element IDs.
 * @returns {string} - The HTML string for the case component.
 */
export function renderCaseHTML(dataAttributes: any, instanceId: string): string {
	const variables = dataAttributes['variables']; // Access the single 'case' object directly from 'variables'
	const name = wrapCasePattern(variables.case_name);
	const { nowPrice, wasPrice, discount, preorderDeposit } = getPricingDetails(variables);
	const { stockStatusClass, stockStatusMessage } = getStockStatus(variables);
	const { productType, productImage } = getProductTypeDetails(variables);
	const buttonText = variables.button_text ? variables.button_text : 'Add to basket';
	const toggleCaseContents = variables.toggle_case_contents === 'true';

	// Generate HTML using template literals and map for iterating over bottles
	const bottlesMarkup = variables.bottles
		.map((bottle: Bottle, bottleIndex: number) => {
			const { productType: bottleType, productImage: bottleImage } =
				getProductTypeDetails(bottle);

			// Facets: spirits show country, spiritCategory. Otherwise, show country | grape | style.
			const facetsMarkup =
				(bottleType || '').toLowerCase() === 'spirits'
					? `${bottle.country} | ${bottle.spirit_type}`
					: `${bottle.country} | ${bottle.grape_type} | ${bottle.wine_style}`;

			const avg =
				!isNaN(Number(bottle.average_rating)) && Number(bottle.average_rating) > 0
					? Number(bottle.average_rating).toFixed(1)
					: '0.0';
			const count =
				!isNaN(Number(bottle.rating_count)) && Number(bottle.rating_count) > 0
					? String(Number(bottle.rating_count))
					: '0';

			return `
			<div class="wc-case__bottle ${bottleType}" id="${instanceId}-${bottleIndex}">
				<div class="wc-case__bottle-image-wrapper">
					<span class="wc-case__bottle-qty">x${bottle.bottle_quantity}</span>
					<picture>
						<source srcset="${bottleImage + '.webp'}" type="image/webp">
						<img class="wc-case__bottle-image" src="${bottleImage + '.png'}" loading="lazy" alt="Product Image">
					</picture>
				</div>
				<div class="wc-case__bottle-details-wrapper">
					<span class="wc-case__bottle-name">${bottle.product_name}</span>

					<div class="wc-case__bottle-star-rating" title="${avg} out of 5 stars from ${count} customers">
						<i class="fa-solid fa-star wc-product__star"></i>
						<span class="wc-case__bottle-rating-text">
							<span class="wc-product__rating-score">${avg}</span>
							<span class="wc-product__rating-max">/ 5</span>
						</span>
					</div>

					<div class="wc-case__bottle-facets">
						${facetsMarkup}
					</div>
				</div>
			</div>
		`;
		})
		.join('');

	return `
		<style>
  .wc-case,
  .wc-case_contents {
    opacity: 0;
    visibility: hidden;
    transition: opacity 300ms ease, visibility 300ms ease;
  }

  .wc-case.active,
  .wc-case_contents.active {
    opacity: 1;
    visibility: visible;
  }
  
  .wc-case, .wc-case_contents {
	display: block;
  }

  @media (min-width: 955px) {
    .wc-case {
      display: flex;
    }
  }
</style>


		<div class="wc-case ${productType} ${stockStatusClass}" data-case-type="${variables.case_type}" id="${instanceId}">
			<div class="wc-case__img-wrapper">
				<picture>
					<source srcset="${productImage + '.webp'}" type="image/webp">
					<img class="wc-case__img" src="${productImage + '.png'}" loading="lazy" alt="Product Image" />
				</picture>
			</div>
			<div class="wc-case__details">
				<h3 class="wc-case__name">${name}</h3>
				<div class="wc-case__pricing">
            	${
					stockStatusMessage === 'Pre-sell'
						? `
            		<span class="wc-case__pricing-price">£${preorderDeposit}</span>
				`
						: `	<span class="wc-case__pricing-price">£${nowPrice}</span>
					${wasPrice ? `<span class="wc-case__pricing-was">was £${wasPrice}</span>` : ''}
					${discount > 0 ? `<span class="wc-case__pricing-saving">save ${discount}%</span>` : ''}
				`
				}
				</div>
				${
					variables.show_voucher_label === 'true'
						? `
					<span class="wc-case__voucher-label">Voucher applied</span>
					`
						: ''
				}
				<span class="wc-case__size">${variables.case_size_custom ? variables.case_size_custom : variables.case_size + ' bottles'}</span>
				${
					variables.delivery_message
						? `
								<span class="wc-case__delivery-label">${variables.delivery_message}</span>
								`
						: ''
				}
				<fieldset class="wc-case__options-wrapper">
					<span class="wc-case__fieldset-heading">Choose your case:</span>
					<div class="wc-case__options"><i></i></div>
				</fieldset>
				${
					variables.subscription_compliance_copy
						? `
					<p class="wc-case__signup-compliance-copy">${variables.subscription_compliance_copy}</p>
				`
						: ''
				}
				<form class="wc-case__atb-wrapper wc-case__atb-form">
					<input type="hidden" name="productId" class="wc-case__product-id" value="${variables.id}">
					<input type="hidden" name="productType" class="wc-case__product-type" value="${productType}">
					<button type="submit" class="wc-case__atb-btn">${stockStatusMessage ? stockStatusMessage : buttonText}</button>
				</form>
				<p class="wc-case__copy">${variables.case_listing_copy}</p>
			</div>
		</div>
		<div class="wc-case_contents wc-case_contents-slider${toggleCaseContents ? ' wc-case_contents-hidden' : ''}" data-case-type-contents="${variables.case_type}">
			${
				toggleCaseContents
					? `
				<div class="wc-case_contents--heading">
					<h3>See what's in your ${variables.case_type} Case</h3>
					<a class="wc-case-contents__reveal-btn">Show Contents</a>
				</div>
			`
					: `<h3 class="wc-case_contents--heading">${variables.case_contents_heading}</h3>`
			}
			<div class="wc-case_contents--items">
				<div class="slider__track ${toggleCaseContents ? 'slider__track--transition' : ''}"${toggleCaseContents ? ' style="opacity: 0;"' : ''}>
					${bottlesMarkup}
				</div>
			</div>
		</div>
	`;
}
